/**
 * `deriveSessionEvents` (D154) — what one committed turn tells the platform,
 * read off the state it left behind.
 *
 * @remarks
 * A pure derived read over two states — the one before the input and the one
 * the store committed — in the `deriveJournal` / `deriveVoiceIndex` shape:
 * nothing is written, nothing is stamped, and the same pair of states always
 * yields the same events. There is no second event log to keep in step with
 * the first one, because the engine already keeps the only record that
 * matters: the occurrence tallies (R12) count every executed action, every
 * typed refusal, and every parse failure by name, so the DIFF of the tallies
 * between two states IS the turn's occurrence stream, engine-minted. The rest
 * is read the same way — the room from `progression`, the endings from the
 * D151 history, a question from `session.pendingQuestion`.
 *
 * The one thing state does not hold is the player's words. A `parse_failed`
 * event carries them (trimmed) from the turn's echo — the ruled exception in
 * `analytics.ts` — and nothing else does.
 *
 * @packageDocumentation
 */

import { type AnalyticsEvent, type RuntimeState, UNANSWERED_INPUT_LIMIT } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

/** The tally keys that grew between two states, each with its family and its narrowed half. */
const grownOccurrencesOf = (
	before: RuntimeState,
	after: RuntimeState,
): readonly { readonly detail: string; readonly family: string }[] =>
	Object.entries(after.narrative.occurrenceTallies).flatMap(([key, count]) => {
		const separator = key.indexOf(':');

		// The bare family key grows beside its narrowed one; only the narrowed
		// half names anything.
		if (separator === -1 || count <= (before.narrative.occurrenceTallies[key] ?? 0)) {
			return [];
		}

		return [{ detail: key.slice(separator + 1), family: key.slice(0, separator) }];
	});

/**
 * The player's words for this turn — the newest echo the turn appended.
 *
 * @remarks
 * Every committed input echoes, so the empty fallback is a type-level
 * obligation rather than a turn that happens: `at(-1)` answers
 * `string | undefined` for any array, including one this function has just
 * filtered from a turn that is known to have echoed. The empty string is the
 * right answer anyway — an analytics row with no input is better than a
 * thrown one — so the fallback stays and the exemption says why.
 *
 * @param before - The state the turn started from
 * @param after - The state it committed
 * @returns The echoed input
 */
const echoedInputOf = (before: RuntimeState, after: RuntimeState): string => {
	const echoes = after.session.transcript
		.slice(before.session.transcript.length)
		.filter((entry) => entry.source === 'player')
		.map((entry) => entry.text);
	const newest = echoes.at(-1);

	/* v8 ignore next 3 */
	if (newest === undefined) {
		return '';
	}

	return newest;
};

/**
 * Derive the events one committed input produced.
 *
 * @param bundle - The loaded bundle (endings are read by id)
 * @param before - The state the input was submitted against
 * @param after - The state the store committed
 * @returns The events, in a stable order: what was done, where it led, how it ended
 */
export const deriveSessionEvents = (bundle: EngineGameBundle, before: RuntimeState, after: RuntimeState): readonly AnalyticsEvent[] => {
	const { turnCount } = after.progression;
	const roomId = after.progression.activeRoomId;
	const occurrences = grownOccurrencesOf(before, after).flatMap((occurrence): readonly AnalyticsEvent[] => {
		switch (occurrence.family) {
			case 'command_executed': {
				return [{ actionType: occurrence.detail, kind: 'turn', roomId, turnCount }];
			}
			case 'command_rejected': {
				return [{ kind: 'command_rejected', reason: occurrence.detail, roomId, turnCount }];
			}
			case 'parse_failed': {
				return [
					{
						failureKind: occurrence.detail,
						input: echoedInputOf(before, after).slice(0, UNANSWERED_INPUT_LIMIT),
						kind: 'parse_failed',
						roomId,
						turnCount,
					},
				];
			}
			// A reaction is an easter egg, not a fact about the game a creator acts on.
			default: {
				return [];
			}
		}
	});
	const arrived: readonly AnalyticsEvent[] =
		roomId === before.progression.activeRoomId ? [] : [{ kind: 'room_entered', roomId, turnCount }];
	const endings = after.narrative.selectedEndingIds
		.slice(before.narrative.selectedEndingIds.length)
		.flatMap((endingId): readonly AnalyticsEvent[] => {
			const ending = bundle.indexes.endingsById.get(endingId);

			/* v8 ignore next -- an ending id the index does not know: the loader
			   refuses a pack whose selections name an ending it has not declared,
			   so this is unreachable from a session that loaded. It stays because
			   the alternative is an analytics row describing an ending that does
			   not exist — silence is the honest answer to a corrupt selection. */
			return ending === undefined ? [] : [{ endingId, endingKind: ending.kind, kind: 'ending_reached', turnCount }];
		});
	const asked: readonly AnalyticsEvent[] =
		after.session.pendingQuestion !== undefined && before.session.pendingQuestion === undefined
			? [{ kind: 'question_asked', roomId, turnCount }]
			: [];

	return [...occurrences, ...arrived, ...endings, ...asked];
};
